import {
  APPLICATION_SOURCE_DIMENSION_IDS,
  type ApplicationGenerationLevel,
  type StudioSourceClarificationQuestion,
} from '@/features/source-assessment/source-assessment-contract'
import {
  PROJECT_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION,
  PROJECT_DESIGN_SYSTEM_GENERATION_SETTINGS_KEY,
} from '@/features/design-system/lib/project-design-system-generation-binding'
import type { StudioProject } from '@/services/project-api'
import { isCanonicalAppGenerationSpecRef } from '@/services/block-api-service'
import {
  STUDIO_GENERATION_DESIGN_AXES,
  validateStudioGenerationAuthorityEnvelope,
  type StudioGenerationAuthorityEnvelope,
} from './generation-authority-envelope'

export type StudioGenerationAuthorityReadback =
  | {
      status: 'available'
      envelope: StudioGenerationAuthorityEnvelope
      projectId: string
    }
  | {
      status: 'absent'
      projectId: string
    }
  | {
      status: 'invalid'
      projectId: string
      reasonCode: 'schema-invalid' | 'evidence-ref-invalid'
    }

const PROFILE_IDS = new Set([
  'creator-empty',
  'design-system-only',
  'app-spec-only',
  'partial-mixed',
  'contract-exact',
])
const DESIGN_MODES = new Set([
  'creator-default',
  'user-selected',
  'source-derived',
  'pack-release-exact',
])
const APPLICATION_MODES = new Set([
  'planner-candidate',
  'resolved-planner-spec',
  'provided-spec',
  'partial-provided-spec',
  'locked-exact-spec',
])
const GENERATION_LEVELS = new Set<ApplicationGenerationLevel>([
  'visual-preview',
  'client-prototype',
  'contract-bound-client',
  'integration-ready',
  'production-ready',
])
const SOURCE_ACTIONS = new Set(['proceed', 'ask-first'])
const SOURCE_REASONS = new Set([
  'not-required',
  'ready',
  'clarification-required',
  'assessment-required',
])
const SOURCE_GATE_STATUSES = new Set([
  'ready',
  'candidate',
  'blocked',
  'not-required',
])
const SOURCE_DIMENSION_IDS = new Set<string>(APPLICATION_SOURCE_DIMENSION_IDS)
const DESIGN_AXES = new Set<string>(STUDIO_GENERATION_DESIGN_AXES)

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isNullableNonEmptyString(value: unknown): value is string | null {
  return value === null || isNonEmptyString(value)
}

function isGenerationLevel(
  value: unknown,
): value is ApplicationGenerationLevel | null {
  return value === null || GENERATION_LEVELS.has(value as ApplicationGenerationLevel)
}

function isCanonicalStringList(
  value: unknown,
  allowed?: ReadonlySet<string>,
): value is string[] {
  if (!Array.isArray(value) || !value.every(isNonEmptyString)) return false
  if (allowed && !value.every((entry) => allowed.has(entry))) return false
  const canonical = [...new Set(value)].sort()
  return (
    canonical.length === value.length &&
    canonical.every((entry, index) => entry === value[index])
  )
}

function isCanonicalEvidenceRefs(value: unknown, required: boolean): value is string[] {
  return (
    isCanonicalStringList(value) &&
    (value as string[]).every((entry) => entry === entry.trim()) &&
    (!required || (value as string[]).length > 0)
  )
}

function isQuestion(value: unknown): value is StudioSourceClarificationQuestion {
  if (!isRecord(value)) return false
  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.dimensionId) &&
    SOURCE_DIMENSION_IDS.has(value.dimensionId) &&
    isNonEmptyString(value.question) &&
    isCanonicalStringList(value.decisionOptions) &&
    value.decisionOptions.length > 0 &&
    isNonEmptyString(value.blockedGenerationLevel) &&
    typeof value.priority === 'number' &&
    Number.isFinite(value.priority)
  )
}

function isQuestionList(value: unknown): value is StudioSourceClarificationQuestion[] {
  return Array.isArray(value) && value.every(isQuestion)
}

function hasEnvelopeShape(value: Record<string, unknown>, projectId: string): boolean {
  const subject = isRecord(value.subject) ? value.subject : null
  const source = isRecord(value.sourceSufficiency)
    ? value.sourceSufficiency
    : null
  const design = isRecord(value.designAuthority) ? value.designAuthority : null
  const application = isRecord(value.applicationAuthority)
    ? value.applicationAuthority
    : null
  const rights = isRecord(value.rights) ? value.rights : null
  const ceiling = isRecord(value.claimCeiling) ? value.claimCeiling : null

  if (!subject || !source || !design || !application || !rights || !ceiling) {
    return false
  }

  return (
    value.schemaVersion === 'cogo.studio-generation-authority-envelope.v1' &&
    value.stage === 'final' &&
    isNonEmptyString(value.profileId) &&
    PROFILE_IDS.has(value.profileId) &&
    subject.projectId === projectId &&
    isNullableNonEmptyString(subject.tenantId) &&
    isNonEmptyString(subject.promptRef) &&
    source.schemaVersion ===
      'studio-generation-entry-source-sufficiency-decision.v1' &&
    isNonEmptyString(source.action) &&
    SOURCE_ACTIONS.has(source.action) &&
    isNonEmptyString(source.reason) &&
    SOURCE_REASONS.has(source.reason) &&
    isNonEmptyString(source.sourceGateStatus) &&
    SOURCE_GATE_STATUSES.has(source.sourceGateStatus) &&
    isNullableNonEmptyString(source.assessmentRevision) &&
    isGenerationLevel(source.requestedGenerationLevel) &&
    isGenerationLevel(source.allowedGenerationLevel) &&
    isCanonicalStringList(source.blockerDimensionIds, SOURCE_DIMENSION_IDS) &&
    isCanonicalStringList(source.clarificationRequestIds) &&
    isQuestionList(source.questions) &&
    source.scoreIsAuthority === false &&
    isNonEmptyString(design.mode) &&
    DESIGN_MODES.has(design.mode) &&
    isNonEmptyString(design.authorityRef) &&
    isCanonicalStringList(design.lockedAxes, DESIGN_AXES) &&
    isCanonicalStringList(design.unresolvedAxes, DESIGN_AXES) &&
    isNonEmptyString(application.mode) &&
    APPLICATION_MODES.has(application.mode) &&
    isCanonicalAppGenerationSpecRef(application.appSpecRef) &&
    isCanonicalStringList(application.lockedPaths) &&
    isCanonicalStringList(application.openSlots) &&
    typeof rights.previewAllowed === 'boolean' &&
    typeof rights.sameProjectPublicationAllowed === 'boolean' &&
    typeof rights.packPromotionAllowed === 'boolean' &&
    isQuestionList(value.questions) &&
    isGenerationLevel(ceiling.maxGenerationLevel) &&
    typeof ceiling.exactVisualConformanceRequired === 'boolean' &&
    typeof ceiling.sourceReadbackRequired === 'boolean'
  )
}

function hasCanonicalEvidence(value: Record<string, unknown>): boolean {
  const source = value.sourceSufficiency as Record<string, unknown>
  const design = value.designAuthority as Record<string, unknown>
  const application = value.applicationAuthority as Record<string, unknown>
  const rights = value.rights as Record<string, unknown>
  const ceiling = value.claimCeiling as Record<string, unknown>

  return (
    isCanonicalEvidenceRefs(
      source.evidenceRefs,
      ceiling.sourceReadbackRequired === true,
    ) &&
    isCanonicalEvidenceRefs(design.evidenceRefs, true) &&
    isCanonicalEvidenceRefs(application.evidenceRefs, true) &&
    isCanonicalEvidenceRefs(rights.receiptRefs, true)
  )
}

type PersistedSourceReceiptReadback =
  | { status: 'absent' }
  | { status: 'invalid' }
  | { status: 'available'; receipt: Record<string, unknown> }

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0
}

function readPersistedSourceReceipt(
  settings: Record<string, unknown>,
): PersistedSourceReceiptReadback {
  if (
    !Object.prototype.hasOwnProperty.call(
      settings,
      PROJECT_DESIGN_SYSTEM_GENERATION_SETTINGS_KEY,
    )
  ) {
    return { status: 'absent' }
  }

  const receipt = settings[PROJECT_DESIGN_SYSTEM_GENERATION_SETTINGS_KEY]
  if (!isRecord(receipt)) return { status: 'invalid' }
  if (
    receipt.schemaVersion !==
      PROJECT_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION ||
    receipt.source !== 'project-design-system-registration' ||
    !isNonEmptyString(receipt.designSystemId) ||
    !isNonEmptyString(receipt.sourceProjectId) ||
    !isNonEmptyString(receipt.sourceId) ||
    !isNonEmptyString(receipt.fileSha256) ||
    !isNonNegativeInteger(receipt.sourceRevision) ||
    !isNonNegativeInteger(receipt.workspaceRevision) ||
    !isNonEmptyString(receipt.registrationContentHash) ||
    !isNonNegativeInteger(receipt.projectedTokenCount) ||
    receipt.previewOnly !== true ||
    receipt.productionGenerationAllowed !== false ||
    receipt.packPromotionAllowed !== false
  ) {
    return { status: 'invalid' }
  }

  return { status: 'available', receipt }
}

function exceedsPersistedSourceReceipt(
  sourceReceipt: Extract<
    PersistedSourceReceiptReadback,
    { status: 'available' }
  >,
  envelope: Record<string, unknown>,
): boolean {
  const receipt = sourceReceipt.receipt
  const rights = envelope.rights as Record<string, unknown>
  return (
    (rights.previewAllowed === true && receipt.previewOnly !== true) ||
    (rights.sameProjectPublicationAllowed === true &&
      receipt.productionGenerationAllowed !== true) ||
    (rights.packPromotionAllowed === true &&
      receipt.packPromotionAllowed !== true)
  )
}

function invalid(
  projectId: string,
  reasonCode: 'schema-invalid' | 'evidence-ref-invalid',
): StudioGenerationAuthorityReadback {
  return { status: 'invalid', projectId, reasonCode }
}

/**
 * Reads the persisted authority receipt without normalizing or guessing it.
 * Legacy projects remain usable as `absent`; a present but malformed receipt
 * fails closed so presentation code cannot continue generation from invented
 * authority.
 */
export function readStudioGenerationAuthority(
  project: StudioProject,
): StudioGenerationAuthorityReadback {
  const projectId = project.id
  const settings = isRecord(project.settings) ? project.settings : null
  const sourceReceipt = settings
    ? readPersistedSourceReceipt(settings)
    : ({ status: 'absent' } as const)
  if (sourceReceipt.status === 'invalid') {
    return invalid(projectId, 'evidence-ref-invalid')
  }
  if (!settings || settings.generationAuthority === undefined) {
    return { status: 'absent', projectId }
  }

  const candidate = settings.generationAuthority
  if (!isRecord(candidate) || !hasEnvelopeShape(candidate, projectId)) {
    return invalid(projectId, 'schema-invalid')
  }
  if (
    !hasCanonicalEvidence(candidate) ||
    (sourceReceipt.status === 'available' &&
      exceedsPersistedSourceReceipt(sourceReceipt, candidate))
  ) {
    return invalid(projectId, 'evidence-ref-invalid')
  }

  try {
    validateStudioGenerationAuthorityEnvelope(
      candidate as unknown as StudioGenerationAuthorityEnvelope,
    )
  } catch {
    return invalid(projectId, 'schema-invalid')
  }

  return {
    status: 'available',
    projectId,
    envelope: structuredClone(
      candidate,
    ) as unknown as StudioGenerationAuthorityEnvelope,
  }
}
